import { useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { CampoDoPasso, JanelaEmBlocos, UFS } from './PassoAPasso.tsx';
import { usarFormulario } from '../lib/formulario.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { maiuscula } from '../lib/caixa.ts';
import type { Prospeccao } from '../lib/api.ts';

/**
 * NOVA EMPRESA e EDITAR EMPRESA no funil — 20/09/2026 (PRD §49.9).
 *
 * Até aqui as 120 empresas só entravam pelo importador, e nenhuma podia ser
 * corrigida pela tela: a rota de editar existia e **não tinha botão**. ⛔ É o
 * defeito que esta casa já catalogou em `sincronizar` — *"método sem tela é
 * defeito"*, e foi assim que uma rota passou semanas invisível.
 *
 * ⚠️ **Cadastrar é do colaborador também** (o trabalho do dia traz empresa
 * nova); ⛔ **editar é do master**, e quem recusa é o servidor, não o botão
 * escondido.
 *
 * ⚠️ **A etapa não se escolhe aqui.** Empresa nova nasce em *A contatar*, e
 * mudar de etapa é o botão da ficha — ato de trabalho, com data no histórico.
 * Editar cadastro e mover o funil são coisas diferentes, e misturá-las faria a
 * correção de um telefone parecer avanço comercial.
 */

const GRUPOS: Array<[string, string]> = [
  ['A-escritorio', 'Escritórios de advocacia'],
  ['B-fundo', 'Fundos (FIDC)'],
  ['B-servicer', 'Servicers'],
  ['B-fomento', 'Fomento mercantil'],
  ['C-credor', 'Credores com jurídico próprio'],
  ['D-parceiro', 'Parceiros de indicação'],
];

export type DadosDaProspeccao = {
  nome: string;
  documento: string;
  cidade: string;
  uf: string;
  grupo: string;
  perfil: string;
  site: string;
  linkedin: string;
  modelo: string;
  sinal: string;
  ressalva: string;
  ordem: number | null;
};

function daProspeccao(p: Prospeccao | null): DadosDaProspeccao {
  return {
    nome: p?.nome ?? '',
    documento: p?.documento ?? '',
    cidade: p?.cidade ?? '',
    uf: p?.uf ?? '',
    grupo: p?.grupo ?? '',
    perfil: p?.perfil ?? '',
    site: p?.site ?? '',
    linkedin: p?.linkedin ?? '',
    modelo: p?.modelo ?? '',
    sinal: p?.sinal ?? '',
    ressalva: p?.ressalva ?? '',
    ordem: p?.ordem ?? null,
  };
}

export function FormularioProspeccao({
  inicial,
  aoFechar,
  aoSalvar,
}: {
  inicial: Prospeccao | null;
  aoFechar: () => void;
  aoSalvar: (dados: DadosDaProspeccao) => Promise<void>;
}) {
  const editando = inicial !== null;
  const [d, setD] = useState<DadosDaProspeccao>(() => daProspeccao(inicial));
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  /* Trava de clique duplo, como no resto da casa: `useState` não serve porque o
     segundo clique pode chegar antes do React repintar o botão desabilitado. */
  const emVoo = useRef(false);

  const guarda = usarFormulario(d, aoFechar);
  usarDialogo(guarda.fechar);

  /** Mexeu no campo, o aviso sai: erro velho na tela manda corrigir o que já está certo. */
  const muda = <K extends keyof DadosDaProspeccao>(k: K, v: DadosDaProspeccao[K]) => {
    setErro(null);
    setD((x) => ({ ...x, [k]: v }));
  };

  function enviar(evento: FormEvent | MouseEvent) {
    evento.preventDefault();
    if (emVoo.current) return;
    if (!d.nome.trim()) return setErro('Escreva o nome da empresa.');
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      await aoSalvar({
        ...d,
        nome: d.nome.trim(),
        site: d.site.trim(),
        linkedin: d.linkedin.trim(),
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui salvar.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  return (
    <>
      <JanelaEmBlocos
        rotulo="Empresa do funil"
        sobrelinha={editando ? 'Comercial · editar empresa' : 'Comercial · nova empresa'}
        titulo={editando ? 'Editar empresa' : 'Nova empresa no funil'}
        sub={
          editando
            ? 'A etapa não muda por aqui: ela é botão da ficha, com data no histórico.'
            : 'Nasce em A contatar. Só o nome é obrigatório; quem decide e os contatos entram na ficha.'
        }
        largura="media"
        aoFechar={guarda.fechar}
        erro={erro}
        rodape={
          <>
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
              Cancelar
            </button>
            <span className="imv-esp" />
            <button type="button" className="imv-bt primario" disabled={salvando} onClick={(e) => void enviar(e)}>
              <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </>
        }
      >
        <div className="imv-grade-campos">
          <CampoDoPasso rotulo="Empresa" obrigatorio col={3}>
            <input
              value={d.nome}
              onChange={(e) => muda('nome', maiuscula(e.target.value))}
              maxLength={200}
              autoFocus={!editando}
            />
          </CampoDoPasso>
          {/* ⭐ Opcional, e vazio é o normal: o levantamento sai de site e de
              artigo, que publicam nome e não documento. ⚠️ Formato errado
              AVISA depois de gravar; não trava o resto do cadastro. */}
          <CampoDoPasso rotulo="CNPJ ou CPF" col={2} mono nota="Opcional.">
            <input
              value={d.documento}
              onChange={(e) => muda('documento', e.target.value)}
              maxLength={20}
              inputMode="numeric"
            />
          </CampoDoPasso>
          <CampoDoPasso rotulo="Cidade" col={2}>
            <input value={d.cidade} onChange={(e) => muda('cidade', maiuscula(e.target.value))} maxLength={120} />
          </CampoDoPasso>
          <CampoDoPasso rotulo="UF" col={1}>
            <select value={d.uf} onChange={(e) => muda('uf', e.target.value)}>
              <option value="">—</option>
              {UFS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </CampoDoPasso>

          <CampoDoPasso rotulo="Grupo" col={3} nota="Em que anel do levantamento ela está.">
            <select value={d.grupo} onChange={(e) => muda('grupo', e.target.value)}>
              <option value="">—</option>
              {GRUPOS.map(([id, nome]) => (
                <option key={id} value={id}>{nome}</option>
              ))}
            </select>
          </CampoDoPasso>
          <CampoDoPasso rotulo="Perfil" col={3} nota="Uma linha sobre o que ela faz.">
            <input value={d.perfil} onChange={(e) => muda('perfil', e.target.value)} maxLength={400} />
          </CampoDoPasso>

          {/*
            ⛔ **Quem decide, e-mail e telefone saíram DAQUI** — 20/09/2026. São
            de PESSOA, e uma empresa tem várias: moram em "Com quem falar", na
            ficha. Deixar uma cópia aqui criaria duas verdades sobre o mesmo
            e-mail, e a primeira correção feita numa delas separaria as duas.
          */}
          <CampoDoPasso rotulo="Site" col={3} mono>
            <input value={d.site} onChange={(e) => muda('site', e.target.value)} maxLength={200} />
          </CampoDoPasso>
          <CampoDoPasso rotulo="LinkedIn" col={3} mono>
            <input value={d.linkedin} onChange={(e) => muda('linkedin', e.target.value)} maxLength={240} />
          </CampoDoPasso>

          <CampoDoPasso rotulo="Modelo de texto" col={2} nota="Qual dos seis usar no primeiro contato.">
            <select value={d.modelo} onChange={(e) => muda('modelo', e.target.value)}>
              <option value="">—</option>
              {['A', 'B', 'C', 'D', 'E', 'F'].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </CampoDoPasso>
          {/* Vazio é "para depois", e não zero: a fila é de quem tem número. */}
          <CampoDoPasso rotulo="Ordem na fila" col={2} mono nota="Vazio é para depois.">
            <input
              type="number"
              min={1}
              max={999}
              value={d.ordem ?? ''}
              onChange={(e) => muda('ordem', e.target.value ? Number(e.target.value) : null)}
            />
          </CampoDoPasso>

          <CampoDoPasso rotulo="Por que vale o contato" col={6}>
            <textarea rows={2} value={d.sinal} onChange={(e) => muda('sinal', e.target.value)} maxLength={1200} />
          </CampoDoPasso>
          <CampoDoPasso rotulo="Ressalva" col={6} nota="O que atrapalha, e quem lê precisa saber antes de escrever.">
            <textarea rows={2} value={d.ressalva} onChange={(e) => muda('ressalva', e.target.value)} maxLength={1200} />
          </CampoDoPasso>
        </div>
      </JanelaEmBlocos>

      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue="a empresa"
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </>
  );
}
